import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, Plus, Trash2, Edit2, X, Check,
  TrendingDown, Calendar, Tag, FileText, Upload,
  ChevronRight, RefreshCw, Filter, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category display config
const CATEGORIES = {
  software: { label: 'Software/AI', color: '#3b82f6', icon: '💻' },
  insurance: { label: 'Insurance', color: '#dc2626', icon: '🛡️' },
  design: { label: 'Design', color: '#8b5cf6', icon: '🎨' },
  marketing: { label: 'Marketing', color: '#10b981', icon: '📣' },
  legal: { label: 'Legal/Admin', color: '#f59e0b', icon: '📋' },
  hosting: { label: 'Hosting/Domain', color: '#0891b2', icon: '🌐' },
  tools: { label: 'Tools/Services', color: '#7c3aed', icon: '🔧' },
  other: { label: 'Other', color: '#6b7280', icon: '📦' }
};

const RECURRING_TYPES = {
  one_time: { label: 'One-time', short: '1x' },
  monthly: { label: 'Monthly', short: '/mo' },
  annual: { label: 'Annual', short: '/yr' },
  quarterly: { label: 'Quarterly', short: '/qtr' }
};

const ExpenseTracker = ({ compact = false, showInTile = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'recurring', 'one-time'
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'software',
    date: new Date().toISOString().split('T')[0],
    recurring_type: 'one_time',
    notes: '',
    receipt_url: ''
  });

  const fetchExpenses = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const [expensesRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/expenses${filterCategory ? `?category=${filterCategory}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/expenses/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (expensesRes.ok) {
        const data = await expensesRes.json();
        setExpenses(data.expenses || []);
      }

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }

      setError(null);
    } catch (err) {
      console.error('Fetch expenses error:', err);
      setError('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingExpense 
        ? `${API_URL}/api/admin/expenses/${editingExpense.id}`
        : `${API_URL}/api/admin/expenses`;
      
      const method = editingExpense ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount)
        })
      });

      if (response.ok) {
        setShowAddForm(false);
        setEditingExpense(null);
        resetForm();
        fetchExpenses();
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to save expense');
      }
    } catch (err) {
      setError('Failed to save expense');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/expenses/${expenseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchExpenses();
      }
    } catch (err) {
      setError('Failed to delete expense');
    }
  };

  const handleEdit = (expense) => {
    setFormData({
      description: expense.description,
      amount: expense.amount.toString(),
      category: expense.category,
      date: expense.date,
      recurring_type: expense.recurring_type,
      notes: expense.notes || '',
      receipt_url: expense.receipt_url || ''
    });
    setEditingExpense(expense);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      category: 'software',
      date: new Date().toISOString().split('T')[0],
      recurring_type: 'one_time',
      notes: '',
      receipt_url: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Compact view for Financial Tile area
  if (showInTile) {
    return (
      <div className="expense-tile-summary" data-testid="expense-tile-summary">
        <div className="expense-tile-header">
          <TrendingDown size={16} />
          <span>Startup Investment</span>
        </div>
        <div className="expense-tile-amount negative">
          -{formatCurrency(summary?.total_invested || 0)}
        </div>
        <div className="expense-tile-meta">
          <span>{summary?.total_expenses || 0} expenses</span>
          <span>•</span>
          <span>{formatCurrency(summary?.monthly_burn_rate || 0)}/mo burn</span>
        </div>
        <button 
          onClick={() => navigate('/admin/expenses')}
          className="expense-tile-link"
          data-testid="view-expenses-link"
        >
          View All <ChevronRight size={14} />
        </button>
      </div>
    );
  }

  // Compact version for stats row
  if (compact) {
    return (
      <div 
        className="admin-stat-card clickable expense-compact" 
        onClick={() => navigate('/admin/expenses')}
        data-testid="expense-tracker-compact"
      >
        <div className="stat-icon expense">
          <TrendingDown size={24} />
        </div>
        <div className="stat-content">
          <span className="stat-value negative">-{formatCurrency(summary?.total_invested || 0)}</span>
          <span className="stat-label">Invested</span>
        </div>
        <ChevronRight className="stat-arrow" size={18} />
      </div>
    );
  }

  // Full view
  return (
    <div className="expense-tracker" data-testid="expense-tracker">
      {/* Header */}
      <div className="expense-header">
        <div className="expense-header-left">
          <h3><TrendingDown size={20} /> Startup Investment Tracker</h3>
          <p className="expense-subtitle">Track all business startup costs</p>
        </div>
        <div className="expense-header-actions">
          <button 
            onClick={() => { resetForm(); setEditingExpense(null); setShowAddForm(true); }}
            className="admin-btn primary"
            data-testid="add-expense-btn"
          >
            <Plus size={16} /> Add Expense
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="expense-summary-grid">
          <div className="expense-summary-card total">
            <div className="summary-icon">
              <DollarSign size={24} />
            </div>
            <div className="summary-content">
              <span className="summary-value negative">-{formatCurrency(summary.total_invested)}</span>
              <span className="summary-label">Total Invested</span>
            </div>
          </div>

          <div className="expense-summary-card">
            <div className="summary-icon burn">
              <RefreshCw size={20} />
            </div>
            <div className="summary-content">
              <span className="summary-value">{formatCurrency(summary.monthly_burn_rate)}</span>
              <span className="summary-label">Monthly Burn</span>
            </div>
          </div>

          <div className="expense-summary-card">
            <div className="summary-icon count">
              <FileText size={20} />
            </div>
            <div className="summary-content">
              <span className="summary-value">{summary.total_expenses}</span>
              <span className="summary-label">Total Expenses</span>
            </div>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {summary && Object.keys(summary.by_category).length > 0 && (
        <div className="expense-category-breakdown">
          <h4>By Category</h4>
          <div className="category-bars">
            {Object.entries(summary.by_category).map(([cat, data]) => {
              const catInfo = CATEGORIES[cat] || CATEGORIES.other;
              const percentage = summary.total_invested > 0 
                ? (data.total / summary.total_invested * 100) 
                : 0;
              return (
                <div key={cat} className="category-bar-item">
                  <div className="category-bar-header">
                    <span className="category-name">
                      {catInfo.icon} {catInfo.label}
                    </span>
                    <span className="category-amount">{formatCurrency(data.total)}</span>
                  </div>
                  <div className="category-bar-track">
                    <div 
                      className="category-bar-fill" 
                      style={{ 
                        width: `${percentage}%`,
                        backgroundColor: catInfo.color
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="expense-filter-row">
        <div className="filter-group">
          <Filter size={16} />
          <select 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
            data-testid="expense-category-filter"
          >
            <option value="">All Categories</option>
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <option key={key} value={key}>{cat.icon} {cat.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="expense-error">
          {error}
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="expense-loading">
          <RefreshCw className="spin" size={24} />
          <span>Loading expenses...</span>
        </div>
      ) : (
        /* Expense List */
        <div className="expense-list">
          {expenses.length === 0 ? (
            <div className="expense-empty">
              <DollarSign size={48} />
              <p>No expenses recorded yet</p>
              <button 
                onClick={() => setShowAddForm(true)}
                className="admin-btn secondary"
              >
                <Plus size={16} /> Add Your First Expense
              </button>
            </div>
          ) : (
            expenses.map((expense) => {
              const catInfo = CATEGORIES[expense.category] || CATEGORIES.other;
              const recurInfo = RECURRING_TYPES[expense.recurring_type] || RECURRING_TYPES.one_time;
              
              return (
                <div key={expense.id} className="expense-item" data-testid={`expense-${expense.id}`}>
                  <div className="expense-item-icon" style={{ backgroundColor: catInfo.color }}>
                    <span>{catInfo.icon}</span>
                  </div>
                  <div className="expense-item-content">
                    <div className="expense-item-main">
                      <span className="expense-description">{expense.description}</span>
                      <span className="expense-amount negative">-{formatCurrency(expense.amount)}</span>
                    </div>
                    <div className="expense-item-meta">
                      <span className="expense-category">{catInfo.label}</span>
                      <span className="expense-date">{formatDate(expense.date)}</span>
                      <span className="expense-recurring">{recurInfo.label}</span>
                      {expense.receipt_url && (
                        <a href={expense.receipt_url} target="_blank" rel="noopener noreferrer" className="expense-receipt">
                          <FileText size={12} /> Receipt
                        </a>
                      )}
                    </div>
                    {expense.notes && (
                      <p className="expense-notes">{expense.notes}</p>
                    )}
                  </div>
                  <div className="expense-item-actions">
                    <button 
                      onClick={() => handleEdit(expense)} 
                      className="expense-action-btn"
                      title="Edit"
                      data-testid={`edit-expense-${expense.id}`}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(expense.id)} 
                      className="expense-action-btn delete"
                      title="Delete"
                      data-testid={`delete-expense-${expense.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="expense-modal-overlay" onClick={() => { setShowAddForm(false); setEditingExpense(null); }}>
          <div className="expense-modal" onClick={(e) => e.stopPropagation()}>
            <div className="expense-modal-header">
              <h3>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</h3>
              <button 
                onClick={() => { setShowAddForm(false); setEditingExpense(null); }}
                className="modal-close-btn"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="expense-form">
              <div className="form-group">
                <label>Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="e.g., Google Workspace, Business Cards"
                  required
                  data-testid="expense-description-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Amount *</label>
                  <div className="input-with-prefix">
                    <span className="input-prefix">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      placeholder="0.00"
                      required
                      data-testid="expense-amount-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                    data-testid="expense-date-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    data-testid="expense-category-input"
                  >
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                      <option key={key} value={key}>{cat.icon} {cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Frequency *</label>
                  <select
                    value={formData.recurring_type}
                    onChange={(e) => setFormData({...formData, recurring_type: e.target.value})}
                    data-testid="expense-recurring-input"
                  >
                    {Object.entries(RECURRING_TYPES).map(([key, rec]) => (
                      <option key={key} value={key}>{rec.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Receipt URL (optional)</label>
                <input
                  type="url"
                  value={formData.receipt_url}
                  onChange={(e) => setFormData({...formData, receipt_url: e.target.value})}
                  placeholder="https://drive.google.com/..."
                  data-testid="expense-receipt-input"
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any additional details..."
                  rows={3}
                  data-testid="expense-notes-input"
                />
              </div>

              <div className="expense-form-actions">
                <button 
                  type="button" 
                  onClick={() => { setShowAddForm(false); setEditingExpense(null); }}
                  className="admin-btn secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="admin-btn primary"
                  data-testid="expense-submit-btn"
                >
                  <Check size={16} /> {editingExpense ? 'Update' : 'Add'} Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseTracker;
